'use client';

import { DefaultPageProps, RequestWebsiteFormValues, RequestWebsiteFormErrors, Themes } from '@/lib/types/shared/page.types';
import { convertMarkdownBolds, convertMarkdownLinks } from '@/lib/utils';
import { Typography, Input, TextareaAutosize } from '@mui/material';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import WebsiteSpecifications from '../WebsiteSpecifications';
import ErrorMessage from "@/components/shared/forms/ErrorMessage";
import { useFormik } from 'formik';
import { sendEmail } from '@/lib/api/EmailApi';
import emailTemplateConfigRequestWebsite from '@/lib/constants/mailTemplates/emailTemplateConfigRequestWebsite';
import { ButtonOne } from '@/components/shared';
import { useWebsite } from '@/components/context/WebsiteContext';
import { Cursor } from '../icons';

type Props = {
    className?: string;
    type?: 'specific' | 'global';
    theme?: Themes;
} & DefaultPageProps;

const ValidateForm = (values: RequestWebsiteFormValues) => {
    const errors: RequestWebsiteFormErrors = {};

    // Name Validation
    if (!values.name) {
        errors.name = 'required';
    } else if (values.name.length < 2) {
        errors.name = 'name_must_be_at_least_2_characters';
    } else {
        if (errors.name) delete errors.name;
    }
    
    // Email Validation
    if (!values.email) {
        errors.email = 'required';
    } else if (!/^[\w%+.-]+@[\d.a-z-]+\.[a-z]{2,4}$/i.test(values.email)) {
        errors.email = 'invalid_email_address';
    } else {
        if (errors.email) delete errors.email;
    }

    // Phone Validation
    if (!values.phone) {
        errors.phone = 'required';
    } else if (values.phone.length < 10) {
        errors.phone = 'phone_must_be_at_least_10_characters';
    } else {
        if (errors.phone) delete errors.phone;
    }

    // Message Validation
    if (!values.wishes) {
        errors.wishes = 'required';
    } else if (values.wishes.length < 10) {
        errors.wishes = 'message_must_be_at_least_10_characters';
    } else {
        if (errors.wishes) delete errors.wishes;
    }

    return errors;
};

const RequestWebsiteForm = ({ className = '', params, type = 'specific', theme = 'light' }: Props) => {
    const { websites, selectedWebsite } = useWebsite();

    const t = useTranslations();
    const t_global = useTranslations();

    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);

    const formik = useFormik({
        initialValues: {
            wishes: '',
            name: '',
            company_name: '',
            email: '',
            phone: '',
        },
          
        validate: ValidateForm,
        
        onSubmit: async (values) => {
            setFormSubmitting(true);

            const emailTemplateConfig = emailTemplateConfigRequestWebsite(values.wishes, values.name, values.company_name, values.email, values.phone, type === 'global' ? 'Algemene quote aanvraag' : websites[selectedWebsite].name);
            sendEmail(emailTemplateConfig.subject, emailTemplateConfig.html).then((response: any) => {
                setFormSubmitting(false);
                setFormSubmitted(true);
            }).catch((error: any) => {
                console.error('error', error);
                setFormSubmitting(false);
            });
        }
    });

    type Event = {
        target: {
            name: string;
            value: string;
        };
    };

    const handleFormChange = (e: Event) => {
        formik.handleChange(e);
    }

    const handleFormBlur = (e: Event) => {
        formik.handleBlur(e);
    }

    const errors = formik.errors;
    const touched = formik.touched;
    const values = formik.values;

    return (
        <form className={className} onSubmit={formik.handleSubmit}>
            <Typography variant="h3" component='h2' className={`${theme === 'light' ? 'text-primary-gray-950' : 'text-white'} capitalize font-bold leading-10 ${type === 'global' && 'text-center'}`}>
                {t('pages.request_website.request_subscription.title')}
            </Typography>

            <hr className='w-full border-primary-gray-950 mb-2' />

            <Typography variant="body1" component='p' className={`${theme === 'light' ? 'text-primary-gray-950' : 'text-white'} ${type === 'global' && 'text-center'} text-wrap`} dangerouslySetInnerHTML={{
                __html: convertMarkdownLinks(convertMarkdownBolds(t('pages.request_website.request_subscription.text')), params.locale)
            }} />



            {formSubmitted ? (
                <>
                    <Typography variant="h4" component='h3' className={`${theme === 'light' ? 'text-primary-gray-950' : 'text-white'} capitalize font-bold leading-10 mt-10 ${type === 'global' && 'text-center'}`}>
                        {t('pages.request_website.request_subscription.thank_you.title')}
                    </Typography>

                    <Typography variant="body1" component='p' className={`${theme === 'light' ? 'text-primary-gray-950' : 'text-white'} ${type === 'global' && 'text-center'}`}>
                        {t('pages.request_website.request_subscription.thank_you.text')}
                    </Typography>
                </>
            ) : (
                <>
                    <Typography variant="h4" component='h3' className={`${theme === 'light' ? 'text-primary-gray-950' : 'text-white'} capitalize font-bold leading-10 mt-10 ${type === 'global' && 'text-center'}`}>
                        {t('pages.request_website.request_subscription.wishes.title')}
                    </Typography>

                    <Typography variant="body1" component='p' className={`${theme === 'light' ? 'text-primary-gray-950' : 'text-white'} ${type === 'global' && 'text-center'}`}>
                        {t('pages.request_website.request_subscription.wishes.text')}
                    </Typography>

                    <div className="field">
                        <div className="transition rounded-xl bg-white border border-primary-gray-300">
                            <TextareaAutosize className='w-full rounded-xl text-primary-gray-950 px-4 py-1 border-none outline-none ring-0 block bg-transparent' name="wishes" placeholder={t('what_would_you_like_to_discuss')} onChange={handleFormChange} onBlur={handleFormBlur} minRows={3} value={values.wishes} required  />
                        </div>
                        <ErrorMessage>{errors.wishes && touched.wishes && t(errors.wishes)}</ErrorMessage>
                    </div>



                    <Typography variant="h4" component='h3' className={`${theme === 'light' ? 'text-primary-gray-950' : 'text-white'} capitalize font-bold leading-10 mt-10 ${type === 'global' && 'text-center'}`}>
                        {t('pages.request_website.request_subscription.personal_details.title')}
                    </Typography>

                    <Typography variant="body1" component='p' className={`${theme === 'light' ? 'text-primary-gray-950' : 'text-white'} ${type === 'global' && 'text-center'}`}>
                        {t('pages.request_website.request_subscription.personal_details.text')}
                    </Typography>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                        <div className="field flex-grow">
                            <div className="transition rounded-xl bg-white border border-primary-gray-300">
                                <Input className='w-full rounded-xl text-primary-gray-950 px-4 py-1' type="text" name="name" placeholder={t('name') + '*'} disableUnderline={true} onChange={handleFormChange} onBlur={handleFormBlur} value={values.name} required />
                            </div>
                            <ErrorMessage>{errors.name && touched.name && t_global(errors.name)}</ErrorMessage>
                        </div>
                        <div className="field flex-grow">
                            <div className="transition rounded-xl bg-white border border-primary-gray-300">
                                <Input className='w-full rounded-xl text-primary-gray-950 px-4 py-1' type="text" name="company_name" placeholder={t('company_name')} disableUnderline={true} onChange={handleFormChange} onBlur={handleFormBlur} value={values.company_name} />
                            </div>
                        </div>
                        <div className="field flex-grow">
                            <div className="transition rounded-xl bg-white border border-primary-gray-300">
                                <Input className='w-full rounded-xl text-primary-gray-950 px-4 py-1' type="email" name="email" placeholder={t_global('email') + '*'} disableUnderline={true} onChange={handleFormChange} onBlur={handleFormBlur} value={values.email} required />
                            </div>
                            <ErrorMessage>{errors.email && touched.email && t_global(errors.email)}</ErrorMessage>
                        </div>
                        <div className="field flex-grow">
                            <div className="transition rounded-xl bg-white border border-primary-gray-300">
                                <Input className='w-full rounded-xl text-primary-gray-950 px-4 py-1' type="tel" name="phone" placeholder={t('phone') + '*'} disableUnderline={true} onChange={handleFormChange} onBlur={handleFormBlur} value={values.phone} required />
                            </div>
                            <ErrorMessage>{errors.phone && touched.phone && t_global(errors.phone)}</ErrorMessage>
                        </div>
                    </div>

                    <ButtonOne paddingX='extra-small' customIcon={
                        <div className='bg-primary-yellow-t rounded-lg p-2'>
                            <Cursor color='fill-primary-yellow-400' />
                        </div>
                    } iconPos='left' variant="green" text={formSubmitting ? t('pages.request_website.request_subscription.sending') : t('pages.request_website.request_subscription.send')} type="button" disabled={formSubmitting} hoverEffect={true} className={`mt-4 ${type === 'global' ? 'mx-auto' : 'ml-0'}`} />
                </>
            )}

            {type !== 'global' && <WebsiteSpecifications />}
        </form>
    );
}

export default RequestWebsiteForm;